// Import i18n system and date utils
import i18n from '../../utils/i18n.js';
import DateUtils from '../../utils/DateUtils.js';

/**
 * BCE Event Ticker Module
 * 
 * Creates a vertical scrolling ticker for upcoming events with smooth animations.
 * Shows event countdown pills, titles, dates and locations with i18n support.
 * 
 * @class BceEventTicker
 */
class BceEventTicker {
    constructor() {
        this.instances = new WeakMap();
        this.autoInit = false;
    }

    /**
     * Initialize all event ticker elements
     */
    init() {
        const tickerContainers = document.querySelectorAll('.bce_event-ticker');
        tickerContainers.forEach(container => {
            if (!this.instances.has(container)) {
                this.create(container);
            }
        });
    }

    /**
     * Create a ticker instance for a container
     * 
     * @param {HTMLElement} container - The container element with bce_event-ticker class
     * @returns {Object|null} Ticker instance or null
     */
    create(container) {
        if (this.instances.has(container)) {
            return this.instances.get(container);
        }

        // Extract events from this container (limit to first 3)
        const events = Array.from(container.querySelectorAll('.articletype-event')).slice(0, 3);
        
        if (events.length === 0) {
            return null;
        }

        // Find event-list-view
        const eventListView = container.querySelector('.event-list-view');
        if (!eventListView) {
            return null;
        }

        // Check if already initialized
        if (eventListView.querySelector('.event-ticker-wrapper')) {
            return null;
        }

        // Extract event data before DOM changes
        const eventData = events.map(event => this.extractEventData(event));

        // Create ticker structure
        this.buildTickerStructure(eventListView, eventData);

        // Initialize ticker functionality
        const tickerInstance = this.initializeTicker(eventListView);

        // Store instance
        this.instances.set(container, tickerInstance);

        return tickerInstance;
    }

    /**
     * Extract data from an event element
     * 
     * @param {HTMLElement} event - The event element
     * @returns {Object} Event data
     */
    extractEventData(event) {
        const headline = event.querySelector('.event-headline')?.textContent?.trim() || 'Event';
        const date = event.querySelector('.event-date')?.textContent?.trim() || '';
        const location = event.querySelector('.event-location')?.textContent?.trim() || '';
        
        // Parse date for pill text
        const startDate = this.parseEventDate(date);
        const pillText = this.generatePillText(startDate);
        
        return { headline, date, location, pillText };
    }

    /**
     * Parse event date from text
     * 
     * @param {string} dateText - The date text
     * @returns {Date|null} Parsed date or null
     */
    parseEventDate(dateText) {
        // Use centralized date parsing utility
        return DateUtils.parseEventDate(dateText);
    }

    /**
     * Generate pill text based on event date using i18n system
     * 
     * @param {Date|null} startDate - The event start date
     * @returns {string} Pill text
     */
    generatePillText(startDate) {
        if (!startDate) {
            return i18n.t('event', 'startingSoon').toUpperCase();
        }

        const now = new Date().getTime();
        const timeLeft = startDate.getTime() - now;
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
        
        if (timeLeft <= 0) {
            return i18n.t('event', 'visitNow').toUpperCase();
        } else if (days === 1) {
            return i18n.t('event', 'daysToGo', 1).toUpperCase();
        } else if (days > 1) {
            return i18n.t('event', 'daysToGo', days).toUpperCase();
        } else {
            return i18n.t('event', 'startingSoon').toUpperCase();
        }
    }

    /**
     * Build ticker DOM structure
     * 
     * @param {HTMLElement} eventListView - The event list container
     * @param {Array} eventData - Array of event data objects
     */
    buildTickerStructure(eventListView, eventData) {
        // Create ticker wrapper
        const tickerWrapper = document.createElement('div');
        tickerWrapper.className = 'event-ticker-wrapper';

        // Create ticker items
        eventData.forEach((data) => {
            const tickerItem = document.createElement('div');
            tickerItem.className = 'event-ticker-item';
            
            tickerItem.innerHTML = `
                <div>
                    <div class="ticker-title">
                        <span class="ticker-pill">${data.pillText}</span>${data.headline}
                    </div>
                    <div class="ticker-meta">
                        <span class="ticker-date">${data.date}</span>
                        <span class="ticker-location">${data.location}</span>
                    </div>
                </div>
            `;
            
            tickerWrapper.appendChild(tickerItem);
        });

        // Create navigation with i18n titles
        const navDiv = document.createElement('div');
        navDiv.className = 'ticker-nav';
        navDiv.innerHTML = `
            <button class="ticker-arrow up" title="${i18n.t('slider', 'previous')}"></button>
            <button class="ticker-arrow down" title="${i18n.t('slider', 'next')}"></button>
        `;

        // Replace original content
        eventListView.innerHTML = '';
        eventListView.appendChild(tickerWrapper);
        eventListView.appendChild(navDiv);
    }

    /**
     * Initialize ticker functionality with animations and controls
     * 
     * @param {HTMLElement} eventListView - The event list container
     * @returns {Object} Ticker instance
     */
    initializeTicker(eventListView) {
        const tickerItems = Array.from(eventListView.querySelectorAll('.event-ticker-item'));
        let currentIndex = 0;
        let autoInterval;

        const nextSlide = () => {
            const currentItem = tickerItems[currentIndex];
            const nextIndex = (currentIndex + 1) % tickerItems.length;
            const nextItem = tickerItems[nextIndex];
            
            // Position next item below
            nextItem.style.transform = 'translateY(100%)';
            nextItem.style.transition = 'none';
            
            // Animate after frame
            requestAnimationFrame(() => {
                nextItem.style.transition = 'transform 0.8s ease-in-out';
                currentItem.style.transform = 'translateY(-100%)'; // Current up
                nextItem.style.transform = 'translateY(0)'; // Next in
                
                currentIndex = nextIndex;
            });
        };

        const prevSlide = () => {
            const currentItem = tickerItems[currentIndex];
            const prevIndex = currentIndex === 0 ? tickerItems.length - 1 : currentIndex - 1;
            const prevItem = tickerItems[prevIndex];
            
            // Position previous item above
            prevItem.style.transform = 'translateY(-100%)';
            prevItem.style.transition = 'none';
            
            // Animate after frame
            requestAnimationFrame(() => {
                prevItem.style.transition = 'transform 0.8s ease-in-out';
                currentItem.style.transform = 'translateY(100%)'; // Current down
                prevItem.style.transform = 'translateY(0)'; // Previous in
                
                currentIndex = prevIndex;
            });
        };

        const startAutoTicker = () => {
            autoInterval = setInterval(nextSlide, 12000); // 12 seconds
        };

        // Navigation event listeners
        const upBtn = eventListView.querySelector('.ticker-arrow.up');
        const downBtn = eventListView.querySelector('.ticker-arrow.down');
        
        upBtn.addEventListener('click', () => {
            clearInterval(autoInterval);
            prevSlide();
            startAutoTicker();
        });
        
        downBtn.addEventListener('click', () => {
            clearInterval(autoInterval);
            nextSlide();
            startAutoTicker();
        });

        // Start auto ticker
        startAutoTicker();

        return {
            currentIndex: () => currentIndex,
            nextSlide,
            prevSlide,
            destroy: () => {
                clearInterval(autoInterval);
            }
        };
    }

    /**
     * Destroy a ticker instance
     * 
     * @param {HTMLElement} container - The ticker container
     */
    destroy(container) {
        const instance = this.instances.get(container);
        if (instance && instance.destroy) {
            instance.destroy();
            this.instances.delete(container);
        }
    }


    /**
     * Get instance for external access
     * 
     * @param {HTMLElement} container - The ticker container
     * @returns {Object|null} Ticker instance or null
     */
    getInstance(container) {
        return this.instances.get(container);
    }
}

export default new BceEventTicker();